import { clearSession } from '../auth/session';

/**
 * Thin data layer. Every resource module (dashboard, users, stations, …) calls
 * `request()` for the real backend and `mock()` while VITE_USE_MOCK_API is on,
 * so screens never need to know which one they're talking to.
 */
export const USE_MOCK = import.meta.env.VITE_USE_MOCK_API === 'true';
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
export const API_LANGUAGE = import.meta.env.VITE_API_LANGUAGE ?? 'du';
// Sent on every request so the backend knows which client and version is calling
const DEVICE_TYPE = 'web';
export const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '1.0';
const TIMEOUT_MS = 15000;

/**
 * `status` is the HTTP status, or 0 when the server couldn't be reached / timed out.
 * `fieldErrors` holds the backend's per-field validation errors (`Data.errors`), if any.
 */
export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
    const errors = data?.Data?.errors ?? data?.data?.errors;
    this.fieldErrors = Array.isArray(errors) ? errors.filter((e) => e && typeof e.field === 'string') : [];
  }
}

/** Joins the base URL (e.g. `http://host:3001/v1/`) and a path (e.g. `admin/onboarding-prices`). */
function apiUrl(path) {
  return `${API_BASE_URL.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/** Sends the admin to /login, remembering the current page so login can return to it. */
function redirectToLogin() {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
}

/**
 * `signal` (optional) lets the caller cancel the request, e.g. when a newer search
 * supersedes it. A caller-cancelled request rejects with the AbortError itself, not ApiError.
 * `auth: false` is for public endpoints: no Authorization header and no 401 redirect.
 * A `FormData` body is sent as-is (multipart); the browser sets its Content-Type with the boundary.
 * `timeoutMs` overrides the default 15 s, e.g. for uploads.
 */
export async function request(path, { method = 'GET', body, headers, signal, auth = true, timeoutMs = TIMEOUT_MS, ...rest } = {}) {
  const token = auth ? localStorage.getItem('token') : null;
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const onCallerAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onCallerAbort, { once: true });
  }

  let res;
  try {
    res = await fetch(apiUrl(path), {
      ...rest,
      method,
      headers: {
        ...(!isForm && { 'Content-Type': 'application/json' }),
        'Accept-Language': API_LANGUAGE,
        // Admin endpoints read the message language from x-lang instead
        'x-lang': API_LANGUAGE,
        deviceType: DEVICE_TYPE,
        version: APP_VERSION,
        ...(token && { Authorization: `Bearer ${token}` }),
        ...headers,
      },
      body: body === undefined || isForm ? body : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError' && !timedOut) throw err;
    throw new ApiError(
      timedOut
        ? 'The server took too long to respond. Please try again.'
        : 'Unable to reach the server. Check your internet connection and try again.',
      0,
    );
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCallerAbort);
  }

  const data = await res.json().catch(() => null);

  // Expired/invalid session: drop it and send the admin back to the login page
  if (res.status === 401 && token) {
    clearSession();
    redirectToLogin();
  }

  // Endpoints use two envelopes: `{ status, message, data }` and `{ Success, Message, Data }`.
  // Both can report failure alongside HTTP 200.
  const failed = data?.status === false || data?.Success === 0 || data?.Success === false;
  if (!res.ok || failed) {
    throw new ApiError(getMessage(data) || `Request failed (${res.status})`, res.status, data);
  }
  return data;
}

export function getMessage(data) {
  return data?.message ?? data?.Message;
}

/** Resolve `data` after a short, realistic delay. */
export function mock(data, delay = 350) {
  return new Promise((resolve) => setTimeout(() => resolve(structuredClone(data)), delay));
}
